import React from 'react';
import classNames from 'classnames';
import PaginationItemsUnbounded from './PaginationItemsUnbounded';
import PaginationItemsDefault from './PaginationItemsDefault';
import { PaginationItemLinkProps } from './PaginationItemLinkProps';
import translations from './translations';
import PaginationArrow from './PaginationArrow';

export interface PaginationProps {
  id?: string;
  className?: string;
  unbounded?: boolean;
  language?: 'en' | 'es';
  /**
   * Optional aria-label for the nav element.
   * @default
   *  en: Pagination
   *  es: Paginación
   */
  label?: string;
  /** Current page number (zero based index) */
  current: number;
  /** Total number of items */
  items: number;
  /**
   * Component that will be rendered for each nav element in the Pagination component
   * Common examples would be react-router <Link /> or <Button variant="unstyled" ... />
   */
  itemLink: (props: PaginationItemLinkProps) => JSX.Element;
  /**
   * Click handler for ItemLinks
   * This is useful if you are using Buttons instead of links for ItemLinks
   */
  itemOnClick?: (page: number) => void;
}

export default function Pagination({
  id,
  className,
  unbounded,
  language = 'en',
  label,
  current,
  items,
  itemLink,
  itemOnClick = () => {},
}: PaginationProps): JSX.Element {
  const variant = unbounded ? 'unbounded' : 'default';
  const labels = translations[language][variant];
  return (
    <nav id={id} aria-label={label || labels.ariaLabel} className={classNames('usa-pagination', className)}>
      <ul className="usa-pagination__list">
        {current > 0 && (
          <PaginationArrow
            variant="previous"
            labels={labels}
            current={current}
            itemLink={itemLink}
            itemOnClick={itemOnClick}
          />
        )}
        {variant === 'default' ? (
          <PaginationItemsDefault
            labels={labels}
            current={current}
            items={items}
            itemLink={itemLink}
            itemOnClick={itemOnClick}
          />
        ) : (
          <PaginationItemsUnbounded
            labels={labels}
            current={current}
            items={items}
            itemLink={itemLink}
            itemOnClick={itemOnClick}
          />
        )}
        {current + 1 !== items && (
          <PaginationArrow
            variant="next"
            labels={labels}
            current={current}
            itemLink={itemLink}
            itemOnClick={itemOnClick}
          />
        )}
      </ul>
    </nav>
  );
}
